import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import type { ServiceNeedsFormData } from '@tasama/types/domain';

/**
 * Thin coordinator store for ServiceNeeds.
 *
 * The full validation logic has moved to react-hook-form inside ServiceNeeds.tsx.
 * This store exists only to:
 *  - Expose isValid to use-step-validation.hook.ts (stepper gating)
 *  - Expose formData to use-onboarding-payload.hook.ts (API payload building)
 *  - Expose resetForm() to store-manager.ts (clearing stores on company type change)
 */

interface ServiceNeedsState extends BaseStoreState {
  formData: ServiceNeedsFormData;
  isValid: boolean;
}

export interface ServiceNeedsStore extends ServiceNeedsState {
  setValidity: (_isValid: boolean) => void;
  setFormData: (_data: Partial<ServiceNeedsFormData>) => void;
  resetForm: () => void;
}

const initialFormData: ServiceNeedsFormData = {
  selectedServices: [],
  engagementTimeframe: '',
  budgetAllocated: '',
  acceptTerms: false,
  establishmentTimeframe: '',
  goalsExpectations: '',
};

export const useServiceNeedsStore = createPersistedStore<ServiceNeedsState>(
  'service-needs',
  {
    formData: initialFormData,
    isValid: false,
  },
  (set) => ({
    setValidity: (isValid: boolean) => set({ isValid }),

    setFormData: (data: Partial<ServiceNeedsFormData>) =>
      set((state: ServiceNeedsState) => ({
        formData: { ...state.formData, ...data },
      })),

    resetForm: () => set({ formData: initialFormData, isValid: false }),
  }),
  {
    partialize: (state: ServiceNeedsState) => ({
      formData: state.formData,
      isValid: state.isValid,
    }),
  }
) as unknown as PersistedStoreHook<ServiceNeedsStore>;

// Selectors — interfaces with use-step-validation and use-onboarding-payload unchanged
export const useServiceNeedsFormData = () =>
  useServiceNeedsStore((state: ServiceNeedsState) => state.formData);
export const useServiceNeedsErrors = () =>
  ({}) as Partial<Record<keyof ServiceNeedsFormData, string>>;
export const useServiceNeedsIsValid = () =>
  useServiceNeedsStore((state: ServiceNeedsState) => state.isValid);
export const useServiceNeedsIsDirty = () => false;
export const useServiceNeedsLoading = () => false;
